<template>
    <div class="panel panel-default panel-dashboard">
        <div class="panel-heading">
            <div class="row">
                <div class="col-xs-15">
                    <h5>
                        Accrued Value
                    </h5>
                </div>

                <div class="col-xs-4">
                    <select-widget
                        v-model="section"
                        class="form-control input-sm"
                        :nullable="true"
                        placeholder="(all)"
                        :options="sectionOptions"
                    ></select-widget>
                </div>

                <div class="col-xs-5 text-right">
                    <button type="button" class="btn btn-black btn-xs" @click="downloadBreakdown"
                            style="margin-top: 5px">
                        <i class="fa fa-download"></i> Download
                    </button>

                    <button type="button" class="btn btn-black btn-xs" @click="expanded = !expanded"
                            style="margin-top: 5px">
                        <i class="fa fa-search"></i> {{ expanded ? 'Collapse' : 'Expand' }}
                    </button>
                </div>
            </div>
        </div>

        <loading-spinner v-if="loading" style="margin: 20px"/>

        <div v-else class="panel-body">
            <canvas width="800" height="250"/>

            <div v-if="expanded">
                <hr>

                <table class="table table-striped table-condensed">
                    <thead>
                    <tr>
                        <th class="col-xs-4">
                            <a href="#" @click.prevent="sort('month')">
                                Month <i class="fa fa-sort"></i>
                            </a>
                        </th>

                        <th class="col-xs-5">
                            <a href="#" @click.prevent="sort(indexColumn)">
                                {{ indexColumnHeadline }} <i class="fa fa-sort"></i>
                            </a>
                        </th>

                        <th class="col-xs-5">
                            <a href="#" @click.prevent="sort(targetColumn)">
                                {{ targetColumnHeadline }} <i class="fa fa-sort"></i>
                            </a>
                        </th>

                        <th class="col-xs-5">
                            <a href="#" @click.prevent="sort(valueColumn)">
                                {{ valueColumnHeadline }} <i class="fa fa-sort"></i>
                            </a>
                        </th>

                        <th class="col-xs-5">
                            <a href="#" @click.prevent="sort(differenceColumn)">
                                Difference <i class="fa fa-sort"></i>
                            </a>
                        </th>
                    </tr>
                    </thead>

                    <tbody>
                    <tr v-for="row in sortedData">
                        <td>
                            {{ row.month_name }}
                        </td>

                        <td>
                            <index-number :value="row[indexColumn]"/>
                        </td>

                        <td>
                            {{ row[targetColumn] | price }}
                        </td>

                        <td>
                            {{ row[valueColumn] | price }}
                        </td>

                        <td>
                            <difference-number :value="row[differenceColumn]"/>
                        </td>
                    </tr>
                    </tbody>

                    <tfoot>
                    <tr>
                        <td>
                            <b>
                                Total
                            </b>
                        </td>

                        <td></td>

                        <td>
                            <b>
                                {{ data.reduce((sum, row) => sum + row[targetColumn], 0) | price }}
                            </b>
                        </td>

                        <td>
                            <b>
                                {{ data.reduce((sum, row) => sum + row[valueColumn], 0) | price }}
                            </b>
                        </td>

                        <td>
                            <b>
                                {{ data.reduce((sum, row) => sum + row[differenceColumn], 0) | price }}
                            </b>
                        </td>
                    </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    </div>
</template>

<script>
const totalizer = {
    id: 'totalizer',

    beforeUpdate: chart => {
        let totals = {}
        let utmost = null

        chart.data.datasets.forEach((dataset, datasetIndex) => {
            if (chart.isDatasetVisible(datasetIndex) && datasetIndex > 0) {
                utmost = datasetIndex

                dataset.data.forEach((value, index) => {
                    totals[index] = (totals[index] || 0) + value
                })
            }
        })

        chart.$totalizer = {
            totals: totals,
            utmost: utmost
        }
    }
}

import http from '@/http'
import store from '@/store'
import axios from 'axios'
import Chart from 'chart.js'
import datalabels from 'chartjs-plugin-datalabels'
import DifferenceNumber from '@/components/Numbers/DifferenceNumber'
import IndexNumber from '@/components/Numbers/IndexNumber'
import LoadingSpinner from '@/components/Layout/LoadingSpinner'
import SelectWidget from '@/vendor/forms/FormGroups/Widgets/Select'
import download from 'downloadjs'

export default {
    components: {DifferenceNumber, IndexNumber, LoadingSpinner, SelectWidget},

    props: {
        year: {
            required: true
        },

        portal: {
            required: true
        },

        user: {
            required: true
        },

        subscriberType: {
            required: true
        }
    },

    data() {
        return {
            data: null,
            loading: true,
            expanded: localStorage.getItem('dashboards/subscriptions/accrued-value/expanded') === 'true',

            chart: null,
            source: null,

            section: null,
            sortBy: 'month',
            sortDirection: -1,

            shared: store
        }
    },

    mounted() {
        this.load()
    },

    watch: {
        year() {
            this.load()
        },

        portal() {
            this.load()
        },

        user() {
            this.load()
        },

        subscriberType() {
            this.load()
        },

        section() {
            this.$nextTick(this.draw)
        },

        expanded(value) {
            localStorage.setItem('dashboards/subscriptions/accrued-value/expanded', value)
        }
    },

    computed: {
        sortedData() {
            return this.data.sort((a, b) => {
                return a[this.sortBy] > b[this.sortBy] ? -this.sortDirection : this.sortDirection
            })
        },

        sectionOptions() {
            return [
                {value: 'new', label: 'New'},
                {value: 'renewal', label: 'Renewal'}
            ]
        },

        datasets() {
            if (this.section === 'new') {
                return [
                    {
                        type: 'line',
                        fill: false,
                        label: 'Target (New)',
                        data: this.data.map(row => row.new_target),
                        backgroundColor: '#20aaea',
                        borderColor: '#20aaea'
                    },
                    {
                        label: 'New (actual)',
                        data: this.data.map(row => row.actual_new_value),
                        backgroundColor: '#62d26f',
                        borderColor: '#62d26f',
                        fill: false
                    },
                    {
                        label: 'New (expected)',
                        data: this.data.map(row => row.expected_new_value),
                        backgroundColor: '#37c5ab',
                        borderColor: '#37c5ab',
                        fill: false
                    }
                ]
            }

            if (this.section === 'renewal') {
                return [
                    {
                        type: 'line',
                        fill: false,
                        label: 'Target (Renewal)',
                        data: this.data.map(row => row.renewal_target),
                        backgroundColor: '#8da3a6',
                        borderColor: '#8da3a6'
                    },
                    {
                        label: 'Renewed',
                        data: this.data.map(row => row.renewal_value),
                        backgroundColor: '#a4cf30',
                        borderColor: '#a4cf30',
                        fill: false
                    },
                    {
                        label: 'Expected',
                        data: this.data.map(row => row.expected_value),
                        backgroundColor: '#eec300',
                        borderColor: '#eec300',
                        fill: false
                    }
                ]
            }

            return [
                {
                    type: 'line',
                    fill: false,
                    label: 'Target (total)',
                    data: this.data.map(row => row.total_target),
                    backgroundColor: '#8da3a6',
                    borderColor: '#8da3a6'
                },
                {
                    label: 'New (actual)',
                    data: this.data.map(row => row.actual_new_value),
                    backgroundColor: '#62d26f',
                    borderColor: '#62d26f',
                    fill: false
                },
                {
                    label: 'New (expected)',
                    data: this.data.map(row => row.expected_new_value),
                    backgroundColor: '#37c5ab',
                    borderColor: '#37c5ab',
                    fill: false
                },
                {
                    label: 'Renewed',
                    data: this.data.map(row => row.renewal_value),
                    backgroundColor: '#a4cf30',
                    borderColor: '#a4cf30',
                    fill: false
                },
                {
                    label: 'Expected',
                    data: this.data.map(row => row.expected_value),
                    backgroundColor: '#eec300',
                    borderColor: '#eec300',
                    fill: false
                }
            ]
        },

        indexColumn() {
            if (this.section === 'new') {
                return 'new_index'
            }

            if (this.section === 'renewal') {
                return 'total_renewal_index'
            }

            return 'total_index'
        },

        targetColumn() {
            if (this.section === 'new') {
                return 'new_target'
            }

            if (this.section === 'renewal') {
                return 'total_renewal_target'
            }

            return 'total_target'
        },

        valueColumn() {
            if (this.section === 'new') {
                return 'new_value'
            }

            if (this.section === 'renewal') {
                return 'total_renewal_value'
            }

            return 'total_value'
        },

        differenceColumn() {
            if (this.section === 'new') {
                return 'new_difference'
            }

            if (this.section === 'renewal') {
                return 'total_renewal_difference'
            }

            return 'total_difference'
        },

        indexColumnHeadline() {
            if (this.section === 'new') {
                return 'Index (New)'
            }

            if (this.section === 'renewal') {
                return 'Index (Renewal)'
            }

            return 'Index (Total)'
        },

        targetColumnHeadline() {
            if (this.section === 'new') {
                return 'Target (New)'
            }

            if (this.section === 'renewal') {
                return 'Target (Renewal)'
            }

            return 'Target (Total)'
        },

        valueColumnHeadline() {
            if (this.section === 'new') {
                return 'Value (New)'
            }

            if (this.section === 'renewal') {
                return 'Value (Renewal + Expected)'
            }

            return 'Value (Total)'
        }
    },

    methods: {
        load() {
            if (this.source) {
                this.source.cancel()
            }

            this.source = axios.CancelToken.source()

            this.loading = true

            return http.post('/api/v1/reports/subscription-accrued-value', {
                from: this.year + '-01-01',
                to: this.year + '-12-31',
                portal_id: this.portal,
                user_id: this.user,
                subscriber_type: this.subscriberType
            }, {cancelToken: this.source.token})
                .then(response => {
                    this.data = response.data.data.map(row => ({
                        month: row.month,
                        month_name: row.month_name,

                        renewal_value: row.renewal_value,
                        expected_value: row.expected_value,
                        renewal_target: row.renewal_target,

                        actual_new_value: row.actual_new_value,
                        expected_new_value: row.expected_new_value,
                        new_value: row.actual_new_value + row.expected_new_value,
                        new_target: row.new_target,
                        new_index: row.new_target ? (row.actual_new_value + row.expected_new_value) / row.new_target * 100 : null,
                        new_difference: row.actual_new_value + row.expected_new_value - row.new_target,

                        total_renewal_value: row.renewal_value + row.expected_value,
                        total_renewal_target: row.renewal_target,
                        total_renewal_index: row.renewal_target ? (row.renewal_value + row.expected_value) / row.renewal_target * 100 : null,
                        total_renewal_difference: (row.renewal_value + row.expected_value) - row.renewal_target,

                        total_value: row.actual_new_value + row.expected_new_value + row.renewal_value + row.expected_value,
                        total_target: row.new_target + row.renewal_target,
                        total_index: (row.new_target + row.renewal_target) ? (row.actual_new_value + row.expected_new_value + row.renewal_value + row.expected_value) / (row.new_target + row.renewal_target) * 100 : null,
                        total_difference: (row.actual_new_value + row.expected_new_value + row.renewal_value + row.expected_value) - (row.new_target + row.renewal_target)
                    }))

                    this.loading = false

                    this.$nextTick(this.draw)
                })
        },

        sort(key) {
            if (this.sortBy === key) {
                return this.sortDirection = -this.sortDirection
            }

            this.sortDirection = key === 'month' ? -1 : 1

            this.sortBy = key
        },

        draw() {
            if (this.chart) {
                this.chart.destroy()
            }

            this.chart = new Chart(this.$el.querySelector('canvas').getContext('2d'), {
                plugins: [datalabels, totalizer],

                type: 'bar',

                data: {
                    labels: this.data.map(row => row.month_name),
                    datasets: this.datasets
                },

                options: {
                    scales: {
                        xAxes: [{
                            stacked: true
                        }],
                        yAxes: [
                            {
                                stacked: true,
                                ticks: {
                                    beginAtZero: true,
                                    callback: value => this.$options.filters.price(value)
                                }
                            }
                        ]
                    },

                    layout: {
                        padding: {
                            top: 25
                        }
                    },

                    legend: {
                        position: 'bottom'
                    },

                    plugins: {
                        datalabels: {
                            anchor: 'end',
                            align: 'end',
                            formatter: (value, ctx) => this.$options.filters.price(ctx.chart.$totalizer.totals[ctx.dataIndex]),
                            display: (ctx) => ctx.datasetIndex === ctx.chart.$totalizer.utmost
                        }
                    },

                    tooltips: {
                        callbacks: {
                            label: (t, d) => {
                                return d.datasets[t.datasetIndex].label + ': ' + this.$options.filters.price(d.datasets[t.datasetIndex].data[t.index])
                            }
                        }
                    }
                }
            })
        },

        downloadBreakdown() {
            axios.post(
                '/api/v1/reports/subscription-accrued-value-breakdown',
                {
                    from: this.year + '-01-01',
                    to: this.year + '-12-31',
                    portal_id: this.portal,
                    user_id: this.user,
                    subscriber_type: this.subscriberType
                },
                {
                    headers: {
                        'Authorization': 'Bearer ' + this.shared.token
                    },
                    responseType: 'blob'
                }
            )
                .then(response => {
                    download(response.data, response.headers['content-disposition'].slice(22, -1))
                })
                .catch(this.catchValidationErrors)
        }
    }
}
</script>
